import invariant from "tiny-invariant";

import { useMyScheduleModalContext } from "../contexts/Modal";
import { useMyScheduleContext } from "../contexts/MySchedule";
import { ModalContent } from "../types/myschedule";

import { selectUser } from "../../../../features/auth/authSlice";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import {
    cancelBooking,
    cancelPlaceholderBooking,
    cancelPlaceholderReplacement,
    cancelReplacement,
    requestPlaceholderReplacement,
    requestReplacement,
} from "../../../../lib/api";
import { IAPIResponse, IGame, IUmpire } from "../../../../types/api";

export const useButtonHandlers = () => {
    const {
        setModalContent,
        setShowModal,
        setModalRequestStatus,
        setModalStatusMessage,
    } = useMyScheduleModalContext();
    const { setGames } = useMyScheduleContext();

    const user = useAppSelector(selectUser);
    invariant(user);

    const handleReplacementRequestClick = (
        game: IGame,
        umpireId: IUmpire["id"],
    ) => {
        setModalContent({ game, umpireId, type: "requestreplacement" });
        setShowModal(true);
    };

    const handleReplacementCancelClick = (
        game: IGame,
        umpireId: IUmpire["id"],
    ) => {
        setModalContent({ game, umpireId, type: "cancelreplacement" });
        setShowModal(true);
    };

    const handleCancelBookingClick = (game: IGame, umpireId: IUmpire["id"]) => {
        setModalContent({ game, umpireId, type: "cancelbooking" });
        setShowModal(true);
    };

    const handleModalConfirmClick = async (props: ModalContent) => {
        const { game, umpireId, type } = props;
        const isGameBooking = game.id !== null;

        setModalRequestStatus("pending");
        let request:
            | Promise<
                  IAPIResponse & {
                      game: IGame;
                  }
              >
            | undefined;

        if (isGameBooking) {
            switch (type) {
                case "requestreplacement":
                    request = requestReplacement(game.id, umpireId);
                    break;
                case "cancelreplacement":
                    request = cancelReplacement(game.id, umpireId);
                    break;
                case "cancelbooking":
                    request = cancelBooking(game.id, umpireId);
                    break;
            }
        } else {
            switch (type) {
                case "requestreplacement":
                    request = requestPlaceholderReplacement(
                        game.league,
                        game.date,
                        game.endDate,
                        game.ageLevel.id,
                        game.fieldId,
                        game.diamond,
                        umpireId,
                    );
                    break;
                case "cancelreplacement":
                    request = cancelPlaceholderReplacement(
                        game.league,
                        game.date,
                        game.endDate,
                        game.ageLevel.id,
                        game.fieldId,
                        game.diamond,
                        umpireId,
                    );
                    break;
                case "cancelbooking":
                    request = cancelPlaceholderBooking(
                        game.league,
                        game.date,
                        game.endDate,
                        game.ageLevel.id,
                        game.fieldId,
                        game.diamond,
                        umpireId,
                    );
                    break;
            }
        }
        request &&
            (await request
                .then(response => {
                    const { game } = response;
                    setGames(prev =>
                        type === "cancelbooking"
                            ? prev.filter(g => g.id !== game.id)
                            : prev.map(g => (g.id === game.id ? game : g)),
                    );
                    setModalRequestStatus("success");
                    let status = "";
                    switch (type) {
                        case "requestreplacement":
                            status =
                                "Success! This game has been posted for replacement.";
                            break;
                        case "cancelreplacement":
                            status =
                                "Success! This replacement request has been canceled.";
                            break;
                        case "cancelbooking":
                            status = "Success! This booking has been canceled.";
                            break;
                    }

                    setModalStatusMessage(status);
                })
                .catch(e => {
                    let status = "";
                    switch (type) {
                        case "requestreplacement":
                            status =
                                "This replacement request could not be completed: ";
                            break;
                        case "cancelreplacement":
                            status =
                                "This replacement request could not be canceled: ";
                            break;
                        case "cancelbooking":
                            status = "This booking could not be canceled:";
                            break;
                    }
                    setModalRequestStatus("failed");
                    setModalStatusMessage(status + e);
                }));
    };

    return {
        handleReplacementRequestClick,
        handleModalConfirmClick,
        handleReplacementCancelClick,
        handleCancelBookingClick,
    };
};
